import { useRef, useReducer, useEffect, useMemo } from 'react';
import cx from 'classnames';
import { Text, SearchTextInput, SelectInput } from '@intellecteu/common-ui';
import { ErrorWireframe } from 'app/components/core/error-wireframe';
import { errorTransformer, LogsFilters, OptionLogLevelType, LogLevels } from 'data';
import { idGenerator } from 'ui';
import { LogRow } from './row';
import { Loading } from './loading';
import { reducer, initialState, Constants, StateType } from './state';
import { type UseLogsProps, useLogs } from './hooks';

import s from './styles.module.scss';

type LogsProps = UseLogsProps & {
  noEntriesMessage?: string;
};

export const Logs = ({ nodeName, noEntriesMessage = 'No new entries found.' }: LogsProps) => {
  // TODO: should be tested
  const [state, dispatch] = useReducer<StateType, any>(reducer, initialState);
  const loadingEndRef = useRef<HTMLDivElement>(null);
  const prevLogsLength = useRef(0);
  const isUIShouldScroll = useRef({ filtersWereChanged: false, dataWereUploaded: false });
  const isFirstRender = useRef(true);

  const filters = useMemo(
    (): LogsFilters & { valueForTriggerFetch?: string | null } => ({
      numLogs: 30,
      referenceTimeStamp: state.referenceTimeStamp,
      prevLogs: state.prevLogs,
      filter: state.filter,
      level: state.level.value,
      valueForTriggerFetch: state.valueForTriggerFetch,
    }),
    [state.goingUp, state.referenceTimeStamp, state.prevLogs, state.filter, state.level, state.valueForTriggerFetch],
  );

  const { data: logs, isLogsLoading, errorMessage, isFetched } = useLogs(nodeName, filters);

  const scrollToBottomLogs = () => {
    if (loadingEndRef.current) loadingEndRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  // scroll to the newest items (first render)
  useEffect(() => {
    if (logs.length > 0 && isFirstRender.current) {
      isFirstRender.current = false;

      scrollToBottomLogs();
    }
  }, [logs.length]);

  useEffect(() => {
    isUIShouldScroll.current.filtersWereChanged = true;
    // this flag should be set to true only when data are loaded
    isUIShouldScroll.current.dataWereUploaded = false;
  }, [state.level.value, state.filter]);

  // scroll to the newest items (on filters changes)
  useEffect(() => {
    if (isUIShouldScroll.current.dataWereUploaded && isUIShouldScroll.current.filtersWereChanged) {
      isUIShouldScroll.current.dataWereUploaded = false;
      isUIShouldScroll.current.filtersWereChanged = false;

      scrollToBottomLogs();
    }
  }, [isUIShouldScroll.current.filtersWereChanged, isUIShouldScroll.current.dataWereUploaded]);

  const onScroll = (e) => {
    e.persist();
    const currentScrollHeight = e.target.scrollHeight; // read-only property is a measurement of the height of an element's content, including content not visible on the screen due to overflow.
    const currentScrollY = e.target.scrollTop;
    const currentClientHeight = e.target.clientHeight;

    if (currentScrollY === 0 && !isLogsLoading) {
      // reset flag when scroll to top, need for correct auto scroll work
      if (isUIShouldScroll.current.dataWereUploaded) {
        isUIShouldScroll.current.dataWereUploaded = false;
      }

      prevLogsLength.current = logs.length;

      dispatch({
        type: Constants.GOING_UP,
        payload: {
          goingUp: true,
          referenceTimeStamp: logs?.[0]?.timeStamp || '',
          prevLogs: true,
        },
      });
    } else if (currentScrollHeight - Math.floor(currentScrollY) === currentClientHeight && !isLogsLoading) {
      dispatch({
        type: Constants.GOING_DOWN,
        payload: {
          goingUp: false,
          referenceTimeStamp: logs?.length ? logs[logs.length - 1].timeStamp : '',
          prevLogs: false,
          valueForTriggerFetch: idGenerator(),
        },
      });
    }
  };

  const setSearch = (value: string) => {
    dispatch({
      type: Constants.SET_SEARCH,
      payload: value,
    });
  };

  const setLevel = (v: OptionLogLevelType) => {
    dispatch({
      type: Constants.SET_LEVEL,
      payload: v,
    });
  };

  const disableActions = isLogsLoading;
  const logsLength = logs?.length || 0;
  const hasNoLogs = !logs || logs?.length === 0;

  const goingUpLoading = (
    <Loading
      logs={logs || []}
      index={isLogsLoading ? 1 : undefined}
      text={isLogsLoading ? '' : noEntriesMessage}
      // no need to show this component if new data were uploaded
      isHide={isLogsLoading ? !logs?.length : prevLogsLength.current !== logsLength}
    />
  );

  const goingDownLoading = (
    <Loading
      isDown
      ref={loadingEndRef}
      logs={logs || []}
      index={isLogsLoading ? logs?.length : undefined}
      text={isLogsLoading ? '' : 'Scroll down/up to load new entries.'}
      isHide={isLogsLoading && hasNoLogs}
    />
  );

  const logsContent =
    isFetched && logs.length === 0 ? (
      <div className={s.noResult}>No data found</div>
    ) : (
      <>
        {state.goingUp && goingUpLoading}
        {logs.length === 0 && <Loading logs={[]} isBase />}
        {logs.map((log, index) => (
          <LogRow key={`${log.content}${index}`} log={log} />
        ))}
        {/* SHOW last element in any case */}
        {goingDownLoading}
      </>
    );

  const content = (
    <div
      onScroll={onScroll}
      className={cx(
        s.logsBody,
        isLogsLoading && s.logsBodyDisabled,
        !logs || isLogsLoading || (isFetched && logs.length === 0 && s.logsBodyDefault),
      )}
    >
      <div className={s.logsContainer}>{logs ? logsContent : <Loading logs={[]} isBase />}</div>
    </div>
  );

  return (
    <div className={s.logs}>
      <div className={s.logsHeader}>
        <div className={s.logsHeaderOperations}>
          <SearchTextInput
            className={s.logsHeaderInput}
            onSearchClick={setSearch}
            onClear={setSearch}
            disabled={disableActions}
            placeholder="Specify text filters separated by commas"
          />

          <SelectInput
            className={cx(s.logsHeaderSelect)}
            wrapperClassName={s.logsHeaderSelectMr16}
            options={LogLevels}
            label="Level"
            value={state.level}
            isSearchable={false}
            isDisabled={disableActions}
            onChange={setLevel}
          />

          <Text
            className={s.logsHeaderLabel}
            size={Text.size.s}
            weight={Text.weight.regular}
            color={Text.color.darkGrey}
          >
            {`Showing ${logsLength} log entr${logs?.length === 1 ? 'y' : 'ies'}`}
          </Text>
        </div>
      </div>
      {errorMessage ? <ErrorWireframe message={errorTransformer(errorMessage)} /> : content}
    </div>
  );
};
